import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import StepBuySale from '../components/StepBuySale';
import { FaFacebookF, FaInstagram, FaCheckCircle } from 'react-icons/fa';
import { SiLine, SiTiktok } from 'react-icons/si';
import { colors } from '../helper/constant/colors';

/* ───────── tiny hook: animate on scroll ───────── */
function useVisible(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

/* ───────── Award Card ───────── */
const AwardCard = ({ icon, title, organizer, detail, importance }) => {
  const [ref, visible] = useVisible();
  return (
    <div
      ref={ref}
      style={{
        background: '#fff',
        borderRadius: 20,
        padding: '28px 24px',
        border: '1px solid #e2eaf4',
        boxShadow: '0 4px 24px rgba(0,74,141,0.07)',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        transition: 'transform 0.4s ease, box-shadow 0.4s ease, opacity 0.5s ease',
        transform: visible ? 'translateY(0)' : 'translateY(30px)',
        opacity: visible ? 1 : 0,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-6px)';
        e.currentTarget.style.boxShadow = '0 20px 40px rgba(52,166,247,0.18)';
        e.currentTarget.style.borderColor = '#34a6f7';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 24px rgba(0,74,141,0.07)';
        e.currentTarget.style.borderColor = '#e2eaf4';
      }}
    >
      <div style={{ fontSize: 36 }}>{icon}</div>
      <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: 0 }}>{title}</h3>
      <p
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: '#34a6f7',
          margin: 0,
          textTransform: 'uppercase',
          letterSpacing: 1,
        }}
      >
        {organizer}
      </p>
      <p
        style={{
          fontSize: 15,
          color: '#64748b',
          lineHeight: 1.7,
          margin: 0,
          flex: 1,
        }}
      >
        {detail}
      </p>
      <div
        style={{
          background: '#f0f9ff',
          borderRadius: 10,
          padding: '8px 14px',
          fontSize: 13,
          fontWeight: 600,
          color: '#004a8d',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <FaCheckCircle style={{ color: '#34a6f7' }} />
        {importance}
      </div>
    </div>
  );
};

AwardCard.propTypes = {
  icon: PropTypes.string,
  title: PropTypes.string,
  organizer: PropTypes.string,
  detail: PropTypes.string,
  importance: PropTypes.string,
};

/* ───────── Stat Badge ───────── */
const StatBadge = ({ number, label }) => (
  <div style={{ textAlign: 'center' }}>
    <div
      style={{
        fontSize: 36,
        fontWeight: 800,
        background: 'linear-gradient(135deg,#fff,#ddf1fc)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
      }}
    >
      {number}
    </div>
    <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.8)', fontWeight: 400 }}>{label}</div>
  </div>
);

StatBadge.propTypes = { number: PropTypes.string, label: PropTypes.string };

/* ───────── Main About ───────── */
function About(props) {
  const [introRef, introVisible] = useVisible();
  const [visionRef, visionVisible] = useVisible();

  return (
    <main
      style={{
        fontFamily: "'Kanit', sans-serif",
        background: '#f8faff',
        minHeight: '100vh',
      }}
    >
      {/* ── Google Font ── */}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Kanit:wght@300;400;500;600;700;800&display=swap');`}</style>

      {/* ══════════ HERO ══════════ */}
      <section
        style={{
          background: 'linear-gradient(135deg, #34a6f7 0%, #0077cc 60%, #004a8d 100%)',
          padding: '100px 24px 160px',
          textAlign: 'center',
          color: '#fff',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* decorative blobs */}
        <div
          style={{
            position: 'absolute',
            top: -80,
            right: -80,
            width: 320,
            height: 320,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.06)',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: -60,
            left: -60,
            width: 240,
            height: 240,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.05)',
            pointerEvents: 'none',
          }}
        />

        <div style={{ position: 'relative', zIndex: 2, maxWidth: 860, margin: '0 auto' }}>
          {/* badge */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              borderRadius: 40,
              padding: '6px 20px',
              fontSize: 13,
              fontWeight: 500,
              marginBottom: 28,
              border: '1px solid rgba(255,255,255,0.25)',
            }}
          >
            🏥 &nbsp;B2B HealthTech Platform Thailand
          </div>

          <h1
            style={{
              fontSize: 'clamp(2.2rem,6vw,3.8rem)',
              fontWeight: 800,
              marginBottom: 20,
              lineHeight: 1.2,
            }}
          >
            เกี่ยวกับ&nbsp;
            <span style={{ position: 'relative', display: 'inline-block' }}>
              <span style={{ position: 'relative', zIndex: 1 }}>มียา (MEYA)</span>
              <span
                style={{
                  position: 'absolute',
                  bottom: 4,
                  left: 0,
                  right: 0,
                  height: 10,
                  background: 'rgba(255,255,255,0.25)',
                  borderRadius: 4,
                  zIndex: 0,
                }}
              />
            </span>
          </h1>

          <p
            style={{
              fontSize: 'clamp(1rem,2.5vw,1.25rem)',
              fontWeight: 300,
              color: '#fff',
              opacity: 1,
              lineHeight: 1.7,
              maxWidth: 680,
              margin: '0 auto 48px',
            }}
          >
            แพลตฟอร์ม B2B HealthTech ที่เชื่อมโยงทุกภาคส่วนในระบบสุขภาพไทย
            <br />
            เพื่อระบบนิเวศที่โปร่งใส เท่าเทียม และยั่งยืน
          </p>

          {/* stats row */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 'clamp(24px,6vw,80px)',
              flexWrap: 'wrap',
            }}
          >
            <StatBadge number='2,000+' label='ร้านค้าในระบบ' />
            <StatBadge number='5,000+' label='รายการสินค้า' />
            <StatBadge number='7+' label='รางวัล' />
            <StatBadge number='2023-ปัจจุบัน' label='ปีที่เติบโต' />
          </div>
        </div>

        {/* wave divider */}
        <svg
          viewBox='0 0 1440 80'
          xmlns='http://www.w3.org/2000/svg'
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            display: 'block',
          }}
        >
          <path d='M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z' fill='#f8faff' />
        </svg>
      </section>

      {/* ══════════ CONTENT WRAPPER ══════════ */}
      <div
        style={{
          maxWidth: 1280,
          width: '92%',
          margin: '0 auto',
          paddingBottom: 80,
        }}
      >
        {/* ── Intro Card ── */}
        <div
          ref={introRef}
          style={{
            background: '#fff',
            borderRadius: 28,
            padding: 'clamp(28px,5vw,56px)',
            boxShadow: '0 20px 60px rgba(0,74,141,0.10)',
            marginTop: -60,
            position: 'relative',
            zIndex: 10,
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: 36,
            alignItems: 'center',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
            opacity: introVisible ? 1 : 0,
            transform: introVisible ? 'translateY(0)' : 'translateY(40px)',
          }}
        >
          {/* icon side */}
          <div
            style={{
              width: 90,
              height: 90,
              background: 'linear-gradient(135deg,#34a6f7,#004a8d)',
              borderRadius: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 42,
              flexShrink: 0,
              boxShadow: '0 10px 30px rgba(52,166,247,0.3)',
            }}
          >
            💊
          </div>
          {/* text side */}
          <div>
            <p
              style={{
                fontSize: 'clamp(15px,2.5vw,18px)',
                lineHeight: 1.9,
                color: '#475569',
                marginBottom: 16,
              }}
            >
              <strong style={{ color: colors.blueLight, fontWeight: 700, fontSize: '1.05em' }}>
                มียา (MEYA)
              </strong>{' '}
              คือแพลตฟอร์มซื้อขายสินค้า B2B ด้านสุขภาพ ที่ถูกออกแบบมาเพื่อเป็น{' '}
              <span style={{ color: colors.pinkDark, fontWeight: 700 }}>
                &quot;สะพานเชื่อมโยง&quot;
              </span>{' '}
              ระหว่าง{' '}
              <strong style={{ color: colors.darkBlue }}>
                ร้านขายยา คลินิก โรงพยาบาล และผู้จัดจำหน่ายเวชภัณฑ์
              </strong>
            </p>
            <p
              style={{
                fontSize: 'clamp(15px,2.5vw,18px)',
                lineHeight: 1.9,
                color: '#475569',
                marginBottom: 0,
              }}
            >
              เรามุ่งมั่นช่วยผู้ประกอบการ{' '}
              <strong style={{ color: '#1e293b' }}>
                ลดต้นทุน ลดของค้างสต๊อก และเพิ่มโอกาสเข้าถึงสินค้านวัตกรรม
              </strong>{' '}
              เพื่อให้ทุกธุรกิจสุขภาพตั้งแต่รายเล็กจนถึงรายใหญ่ สามารถเติบโตได้อย่างยั่งยืน
            </p>
          </div>
        </div>

        {/* ── Steps Section ── */}
        <div style={{ marginTop: 80 }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <p
              style={{
                color: '#34a6f7',
                fontWeight: 600,
                fontSize: 13,
                textTransform: 'uppercase',
                letterSpacing: 3,
                marginBottom: 10,
              }}
            >
              HOW IT WORKS
            </p>
            <h2
              style={{
                fontSize: 'clamp(1.6rem,4vw,2.25rem)',
                fontWeight: 800,
                color: '#1e293b',
                margin: 0,
              }}
            >
              การใช้งาน <span style={{ color: '#34a6f7' }}>มียา</span>
            </h2>
            <p
              style={{
                color: '#64748b',
                marginTop: 10,
                fontSize: 16,
              }}
            >
              ขั้นตอนง่ายๆ สำหรับการเริ่มต้นซื้อและขายสินค้าบนแพลตฟอร์ม
            </p>
          </div>

          <div
            style={{
              background: '#fff',
              borderRadius: 28,
              padding: 'clamp(24px,4vw,48px)',
              boxShadow: '0 10px 30px rgba(0,74,141,0.07)',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))',
                gap: 32,
              }}
            >
              <div>
                <StepBuySale
                  stepname='ขั้นตอนการสั่งซื้อสินค้า'
                  step1={{
                    iconclass: 'text-primary mdi mdi-compare-horizontal font-size-46',
                    header: 'COMPARE',
                    text: 'เปรียบเทียบราคาสินค้าจากแต่ละร้านค้า',
                    link: 'https://www.youtube.com/watch?v=WuuSfEI1ovU&index=4&t=27s',
                  }}
                  step2={{
                    iconclass: 'text-primary mdi mdi-shopping-outline font-size-46',
                    header: 'ORDER',
                    text: 'สั่งซื้อสินค้าจากร้านค้าที่ดีที่สุด',
                    link: 'https://www.youtube.com/watch?v=WuuSfEI1ovU&index=4&t=8s',
                  }}
                  step3={{
                    iconclass: 'text-primary mdi mdi-truck-fast-outline font-size-46',
                    header: 'DELIVERY',
                    text: 'รอสินค้าที่จะจัดส่งไปให้คุณจากทั่วประเทศ',
                    link: 'https://www.youtube.com/watch?v=CsLNpDvaD6c&index=2&t=40s',
                  }}
                  step4={{
                    iconclass: 'text-primary mdi mdi-dresser-outline font-size-46',
                    header: 'STOCK',
                    text: 'รับสินค้าที่จัดส่งจากร้านค้าและนำเข้าสต็อค',
                  }}
                  button={{ text: 'ซื้อสินค้า', pathlink: '/marketplace' }}
                  permitdata={props.permitdata}
                  mode={'buyer'}
                />
              </div>
              <div>
                <StepBuySale
                  stepname='ขั้นตอนการขายสินค้า'
                  step1={{
                    iconclass: 'text-primary mdi mdi-store-outline font-size-46',
                    header: 'REGISTER',
                    text: 'ลงทะเบียนร้านค้าของคุณ',
                    link: 'https://www.youtube.com/watch?v=kegDStMlbSU&index=1&t=14s',
                  }}
                  step2={{
                    iconclass: 'text-primary mdi mdi-playlist-plus font-size-46',
                    header: 'LISTING',
                    text: 'ลงขายสินค้าในร้านค้าของคุณ',
                    link: 'https://www.youtube.com/watch?v=RRe_GeTFr9k&index=5&t=27s',
                  }}
                  step3={{
                    iconclass: 'text-primary mdi mdi-file-check-outline font-size-46',
                    header: 'TAKE ORDER',
                    text: 'รับคำสั่งซื้อ',
                    link: 'https://www.youtube.com/watch?v=CsLNpDvaD6c&index=2&t=12s',
                  }}
                  step4={{
                    iconclass: 'text-primary mdi mdi-truck-fast-outline font-size-46',
                    header: 'DELIVERY',
                    text: 'จัดส่งสินค้าตามคำสั่งซื้อ',
                    link: 'https://www.youtube.com/watch?v=CsLNpDvaD6c&index=2&t=40s',
                  }}
                  button={{ text: 'เริ่มขายสินค้า', pathlink: '/myshop/seller/jobtodo' }}
                  permitdata={props.permitdata}
                  mode={'seller'}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Awards Section ── */}
        <section style={{ marginTop: 80 }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <p
              style={{
                color: '#34a6f7',
                fontWeight: 600,
                fontSize: 13,
                textTransform: 'uppercase',
                letterSpacing: 3,
                marginBottom: 10,
              }}
            >
              RECOGNITION
            </p>
            <h2
              style={{
                fontSize: 'clamp(1.6rem,4vw,2.25rem)',
                fontWeight: 800,
                color: '#1e293b',
                margin: 0,
              }}
            >
              รางวัลและความสำเร็จ <span style={{ color: '#34a6f7' }}>ความภูมิใจของเรา</span>
            </h2>
            <p
              style={{
                color: '#64748b',
                marginTop: 10,
                fontSize: 16,
              }}
            >
              รางวัลที่การันตีความมุ่งมั่นและความเชี่ยวชาญในระดับประเทศ
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(270px,1fr))',
              gap: 24,
            }}
          >
            <AwardCard
              icon='🏆'
              title='TED Fund (POC)'
              organizer='สำนักงานนวัตกรรมแห่งชาติ (NIA)'
              detail='ทุน Proof of Concept เพื่อพัฒนาแพลตฟอร์มแก้ปัญหาการจัดซื้อจัดจ้างในระบบสุขภาพ'
              importance='ยืนยันศักยภาพด้านเทคโนโลยีและธุรกิจ'
            />
            <AwardCard
              icon='🚀'
              title='Startup Thailand League'
              organizer='NIA & University Network'
              detail='ได้รับคัดเลือกเป็นสตาร์ทอัพ HealthTech น่าจับตามอง (2023 & 2025)'
              importance='การยอมรับในระดับประเทศ'
            />
            <AwardCard
              icon='🤝'
              title='SIBB 2024'
              organizer='KRIS - KMITL'
              detail='โครงการพัฒนาสตาร์ทอัพเชิงลึก Startup Innovative Business Brotherhood'
              importance='การสนับสนุนจากเครือข่ายชั้นนำ'
            />
            <AwardCard
              icon='🌏'
              title='INT – Mahidol University'
              organizer='มหาวิทยาลัยมหิดล'
              detail='การสนับสนุนเชื่อมโยงผู้เชี่ยวชาญและนักลงทุนต่างชาติ'
              importance='พร้อมก้าวสู่ตลาดสากล'
            />
          </div>
        </section>

        {/* ── Vision Section ── */}
        <section
          ref={visionRef}
          style={{
            marginTop: 80,
            background: 'linear-gradient(135deg,#0f172a 0%,#1e3a5f 100%)',
            borderRadius: 32,
            padding: 'clamp(40px,6vw,72px) clamp(24px,5vw,80px)',
            color: '#fff',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 30px 60px rgba(0,74,141,0.25)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
            opacity: visionVisible ? 1 : 0,
            transform: visionVisible ? 'translateY(0)' : 'translateY(40px)',
          }}
        >
          {/* glow orbs */}
          <div
            style={{
              position: 'absolute',
              top: -60,
              right: -60,
              width: 260,
              height: 260,
              borderRadius: '50%',
              background: 'radial-gradient(circle,rgba(52,166,247,0.2),transparent 70%)',
              pointerEvents: 'none',
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: -80,
              left: -40,
              width: 300,
              height: 300,
              borderRadius: '50%',
              background: 'radial-gradient(circle,rgba(52,166,247,0.12),transparent 70%)',
              pointerEvents: 'none',
            }}
          />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: 4,
                color: '#34a6f7',
                marginBottom: 24,
                textTransform: 'uppercase',
              }}
            >
              OUR VISION
            </div>
            <div style={{ fontSize: 48, marginBottom: 20 }}>🔭</div>
            <p
              style={{
                fontSize: 'clamp(1.1rem,3vw,1.6rem)',
                fontWeight: 600,
                lineHeight: 1.8,
                fontStyle: 'italic',
                maxWidth: 840,
                margin: '0 auto',
                color: 'rgba(255,255,255,0.95)',
              }}
            >
              &quot;มียา จะเป็นโครงสร้างพื้นฐานดิจิทัลที่สำคัญของระบบสาธารณสุขไทย
              <br />
              สร้างระบบนิเวศการซื้อขายที่โปร่งใส ลดความเหลื่อมล้ำ
              <br />
              และกระจายโอกาสทางธุรกิจอย่างเท่าเทียม&quot;
            </p>

            {/* value pills */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 12,
                marginTop: 40,
              }}
            >
              {['โปร่งใส', 'เท่าเทียม', 'ยั่งยืน', 'นวัตกรรม'].map((v) => (
                <span
                  key={v}
                  style={{
                    background: 'rgba(52,166,247,0.15)',
                    border: '1px solid rgba(52,166,247,0.4)',
                    borderRadius: 40,
                    padding: '6px 20px',
                    fontSize: 14,
                    fontWeight: 500,
                    backdropFilter: 'blur(4px)',
                  }}
                >
                  {v}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ── Social Section ── */}
        <section style={{ textAlign: 'center', marginTop: 80 }}>
          <p
            style={{
              color: '#34a6f7',
              fontWeight: 600,
              fontSize: 13,
              textTransform: 'uppercase',
              letterSpacing: 3,
              marginBottom: 10,
            }}
          >
            CONNECT WITH US
          </p>
          <h2
            style={{
              fontSize: 'clamp(1.4rem,3.5vw,2rem)',
              fontWeight: 800,
              color: '#1e293b',
              marginBottom: 8,
            }}
          >
            ติดตามเราได้ที่
          </h2>
          <p
            style={{
              color: '#64748b',
              marginBottom: 36,
              fontSize: 15,
            }}
          >
            รับข่าวสาร โปรโมชั่น และอัปเดตผลิตภัณฑ์ใหม่
          </p>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: 16,
            }}
          >
            {[
              {
                href: 'https://lin.ee/G3gOv00',
                icon: <SiLine />,
                label: 'LINE Official',
                hoverColor: '#06C755',
                bg: '#f0fdf4',
              },
              {
                href: 'https://www.facebook.com/meyadrug?locale=th_TH',
                icon: <FaFacebookF />,
                label: 'Facebook',
                hoverColor: '#1877F2',
                bg: '#eff6ff',
              },
              {
                href: 'https://www.instagram.com/meya.ally.hc',
                icon: <FaInstagram />,
                label: 'Instagram',
                hoverColor: '#E1306C',
                bg: '#fdf2f8',
              },
              {
                href: 'https://www.tiktok.com/@meya.ally.hc?_r=1&_t=ZS-92v1Zme0TzO',
                icon: <SiTiktok />,
                label: 'TikTok',
                hoverColor: '#010101',
                bg: '#f9f9f9',
              },
            ].map(({ href, icon, label, hoverColor, bg }) => (
              <SocialBtn
                key={label}
                href={href}
                icon={icon}
                label={label}
                hoverColor={hoverColor}
                bg={bg}
              />
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}

/* ── Social Button with hover state ── */
function SocialBtn({ href, icon, label, hoverColor, bg }) {
  const [hovered, setHovered] = useState(false);
  return (
    <a
      href={href}
      target='_blank'
      rel='noopener noreferrer'
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '14px 28px',
        background: hovered ? bg : '#fff',
        border: `2px solid ${hovered ? hoverColor : '#e2eaf4'}`,
        borderRadius: 16,
        textDecoration: 'none',
        color: hovered ? hoverColor : '#1e293b',
        fontWeight: 600,
        fontSize: 15,
        boxShadow: hovered ? `0 8px 24px ${hoverColor}33` : '0 2px 8px rgba(0,0,0,0.06)',
        transform: hovered ? 'translateY(-3px)' : 'none',
        transition: 'all 0.25s ease',
        fontFamily: "'Kanit', sans-serif",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span style={{ fontSize: 22 }}>{icon}</span>
      <span>{label}</span>
    </a>
  );
}

SocialBtn.propTypes = {
  href: PropTypes.string,
  icon: PropTypes.node,
  label: PropTypes.string,
  hoverColor: PropTypes.string,
  bg: PropTypes.string,
};

About.propTypes = {
  permitdata: PropTypes.object,
};

export default About;
